import React from 'react'
import { useNavigate } from './useNavigate'
import { useLoader } from './useLoader'
import { useAppointmentsStepsItems } from './useAppointmentsStepsItems'
import { appointmentsService } from '../services/AppointmentsService'
import { profileService } from '../services/ProfileService'
import { store } from '../store'
import { Item } from '../components/screens/screenAppointmentsList/CustomSteps'
import { IAppointmentGetAppointment } from '../interfaces/rpc/IAppointmentGetAppointment'
import { IResourceGetBusinessResources } from '../interfaces/rpc/IResourceGetBusinessResources'

const getSingleDayRange = (date: Date) => {
  const from = new Date(date)
  from.setHours(0, 0, 0, 0)
  const to = new Date(date)
  to.setHours(23, 59, 59, 999)
  return { dateFrom: from, dateTo: to }
}

const getAggregatedRange = (date: Date) => getSingleDayRange(date)

const getAppointmentById = (list: IAppointmentGetAppointment[], id: any) =>
  list.find(ap => ap.appointment.id === id)

export const useAppointmentsListScreenController = () => {
  const navigate = useNavigate()
  const date = store.appointments.selectedDate || new Date()
  const selectedResourceId = store.profile.selectedResourceId
  const selectedBusinessId = store.profile.businessId
  const resourcesByBusinessId = store.profile.resourcesByBusinessId || {}
  const businessesById = store.profile.businessesById
  const hasResources = Object.values(resourcesByBusinessId).some(list => (list || []).length > 0)
  const isAggregated = selectedResourceId === 'all'
  const [datePickerOpen, setDatePickerOpen] = React.useState(false)
  const openDatePicker = React.useCallback(() => setDatePickerOpen(true), [])
  const closeDatePicker = React.useCallback(() => setDatePickerOpen(false), [])
  const changeDate = React.useCallback((value: Date) => {
    store.appointments.selectedDate = value
  }, [])

  const range = React.useMemo(() => (
    isAggregated ? getAggregatedRange(date) : getSingleDayRange(date)
  ), [isAggregated, date.getTime()])


  const params = React.useMemo(() => ({
    mode: isAggregated ? 'all' : 'single',
    resourceId: isAggregated ? null : selectedResourceId,
    businessId: isAggregated ? null : selectedBusinessId,
    dateFrom: range.dateFrom,
    dateTo: range.dateTo,
  }), [
    isAggregated,
    selectedResourceId,
    selectedBusinessId,
    range.dateFrom.getTime(),
    range.dateTo.getTime(),
  ])

  const paramsKey = React.useMemo(() => ([
    params.mode,
    params.resourceId ?? '',
    params.businessId ?? '',
    params.dateFrom.toISOString(),
    params.dateTo.toISOString(),
  ].join('|')), [
    params.mode,
    params.resourceId,
    params.businessId,
    params.dateFrom.getTime(),
    params.dateTo.getTime(),
  ])

  const loadList = React.useCallback(
    (loadParams: Parameters<typeof appointmentsService.loadList>[0]) =>
      appointmentsService.loadList(loadParams),
    []
  )
  const [loading, load] = useLoader(loadList)

  React.useEffect(() => {
    load(params)

  // NOTE: `useLoader` may cause infinite loading in some edge cases.
  // This MR fixes the concrete loading issue in appointment screen,
  // refactoring `useLoader` itself is out of scope and should be tracked separately.
  }, [paramsKey])

  const reload = React.useCallback(() => {
    load(params)

  // NOTE: `useLoader` may cause infinite loading in some edge cases.
  // This MR fixes the concrete loading issue in appointment screen,
  // refactoring `useLoader` itself is out of scope and should be tracked separately.
  }, [paramsKey])

  const sortedList = React.useMemo(() => {
    const list = store.appointments.list || []
    return [...list].sort((a, b) =>
      +new Date(a.appointment.start) - +new Date(b.appointment.start)
    )
  }, [store.appointments.list])

  const { items: stepsItems, current: stepsCurrent } = useAppointmentsStepsItems(sortedList)

  const partialLoadMessages = React.useMemo(() => {
    const errors = store.appointments.loadErrors || []
    return errors
      .map(error => {
        const businessName = store.profile.businessesById[error.businessId]?.name
          || store.profile.profile?.businessData?.[error.businessId]?.name
        return `${businessName || error.businessId}: ${error.message}`
      })
      .join('; ')
  }, [store.appointments.loadErrors, store.profile.businessesById, store.profile.profile?.businessData])

  const swapDates = React.useCallback(
    (swapParams: Parameters<typeof appointmentsService.swapDatesAppointments>[0]) =>
      appointmentsService.swapDatesAppointments(swapParams),
    []
  )
  const [swapping, swap] = useLoader(swapDates)

  const swapAppointments = React.useCallback(async (itemFrom: Item, itemTo: Item) => {
    if (!itemFrom || !itemTo) return
    const firstAppointment = getAppointmentById(sortedList, itemFrom.id)
    const secondAppointment = getAppointmentById(sortedList, itemTo.id)
    if (!firstAppointment || !secondAppointment) return

    await swap({ source: firstAppointment, target: secondAppointment })
    await reload()
  }, [sortedList, swap])

  const selectAllResources = React.useCallback(() => {
    profileService.setSelectedResource('all')
  }, [])

  const selectResource = React.useCallback((resource: IResourceGetBusinessResources[number], businessId: string) => {
    profileService.setResource(resource, businessId)
  }, [])

  const openAppointment = React.useCallback((item: Item) => {
    const context = item.context
    navigate('/appointment-info/:id?businessId=:businessId&resourceId=:resourceId', {
      id: item.id,
      businessId: context?.businessId || selectedBusinessId,
      resourceId: context?.resourceId || store.profile.resource?.id || ''
    } as never)
  }, [navigate, selectedBusinessId])

  return {
    data: {
      date,
      list: sortedList,
      stepsItems,
      stepsCurrent,
      isAggregated,
      partialLoadMessages,
      resourcesByBusinessId,
      businessesById,
      selectedResourceId,
      selectedBusinessId,
      hasResources,
    },
    ui: {
      datePickerOpen,
      openDatePicker,
      closeDatePicker,
    },
    actions: {
      changeDate,
      reload,
      swapAppointments,
      selectAllResources,
      selectResource,
    },
    status: {
      loading,
      swapping,
    },
    navigation: {
      openAppointment,
    }
  }
}
